import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';
import { toast } from 'sonner';

export interface GamificationCampaign {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  reward_type: 'url' | 'text' | 'coupon' | 'ar';
  reward_data: any;
  created_at: string;
  steps?: GamificationStep[];
  participant_count?: number;
  completion_count?: number;
}

export interface GamificationStep {
  id: string;
  campaign_id: string;
  qr_code_id: string;
  step_order: number;
  title: string | null;
  unlock_message: string | null;
  qr_code?: {
    name: string;
    short_slug: string | null;
  };
}

export function useGamification() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: ['gamification-campaigns', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await (supabase as any)
        .from('gamification_campaigns')
        .select(`
          *,
          steps:gamification_steps(
            *,
            qr_code:qr_codes(name, short_slug)
          ),
          progress:gamification_progress(id, is_completed)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
        
      if (error) throw error;

      return (data as any[]).map(campaign => ({
        ...campaign,
        participant_count: campaign.progress?.length || 0,
        completion_count: campaign.progress?.filter((p: any) => p.is_completed).length || 0
      })) as GamificationCampaign[];
    },
    enabled: !!user,
  });

  const saveCampaign = useMutation({
    mutationFn: async (campaign: Partial<GamificationCampaign>) => {
      if (!user) throw new Error('Not authenticated');
      
      const { data, error } = await (supabase as any)
        .from('gamification_campaigns')
        .insert({
          ...campaign,
          user_id: user.id,
        })
        .select()
        .single();
        
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gamification-campaigns'] });
      toast.success('Campaign created successfully!');
    },
    onError: (error) => {
      toast.error('Failed to create campaign: ' + error.message);
    },
  });

  const updateCampaign = useMutation({
    mutationFn: async ({ id, ...campaign }: Partial<GamificationCampaign> & { id: string }) => {
      const { error } = await (supabase as any)
        .from('gamification_campaigns')
        .update(campaign)
        .eq('id', id);
        
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gamification-campaigns'] });
      toast.success('Campaign updated!');
    },
    onError: (error) => {
      toast.error('Failed to update: ' + error.message);
    },
  });

  const deleteCampaign = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any)
        .from('gamification_campaigns')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gamification-campaigns'] });
      toast.success('Campaign deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete: ' + error.message);
    },
  });

  const addStep = useMutation({
    mutationFn: async (step: Partial<GamificationStep>) => {
      const { data, error } = await (supabase as any)
        .from('gamification_steps')
        .insert(step)
        .select()
        .single();
        
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gamification-campaigns'] });
      toast.success('Step added to campaign');
    },
    onError: (error) => {
      toast.error('Failed to add step: ' + error.message);
    },
  });

  const removeStep = useMutation({
    mutationFn: async (stepId: string) => {
      const { error } = await (supabase as any)
        .from('gamification_steps')
        .delete()
        .eq('id', stepId);
        
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gamification-campaigns'] });
      toast.success('Step removed');
    },
    onError: (error) => {
      toast.error('Failed to remove step: ' + error.message);
    },
  });

  return {
    campaigns,
    isLoading,
    saveCampaign,
    updateCampaign,
    deleteCampaign,
    addStep,
    removeStep,
  };
}
